import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectTheme } from "../../../Store/features/design/designSlice";
import { FaTwitter, FaFacebook, FaLinkedin, FaWhatsapp, FaComments } from "react-icons/fa";
import { FiCopy } from "react-icons/fi";
import { getUserConnections, findChat, createChat, addMessage } from "../../../Service/user";
import defaultAvatar from "../../../Images/avatars/image.png";
import { toast } from "react-hot-toast";
import "./SharePopup.scss";

const SharePopup = ({ url, isOpen, setIsOpen, isInvestor }) => {
	const theme = useSelector(selectTheme);
	const [showCopyAlert, setShowCopyAlert] = useState(false);
	const [whatsappTitle, setWhatsappTitle] = useState("");
	const [connections, setConnections] = useState([]);
	const [showConnectionsModal, setShowConnectionsModal] = useState(false);
	const [selectedConnections, setSelectedConnections] = useState([]);
	const [messageText, setMessageText] = useState("Hey, this is my meeting/webinar/event/dm link you might be interested in!");
	const [sendingMessages, setSendingMessages] = useState(false);
	const loggedInUser = useSelector((state) => state.user.loggedInUser);

	// Fetch connections when component mounts
	useEffect(() => {
		if (isOpen && loggedInUser?._id) {
			fetchConnections();
		}
	}, [isOpen, loggedInUser?._id]);

	const fetchConnections = async () => {
		try {
			const response = await getUserConnections(loggedInUser._id);
			setConnections(response.data || []);
		} catch (error) {
			console.error("Error fetching connections:", error);
		}
	};

	const handleCopyLink = async () => {
		try {
			await navigator.clipboard.writeText(url);
			setShowCopyAlert(true);
			setTimeout(() => setShowCopyAlert(false), 2000);
		} catch (err) {
			console.error("Failed to copy link:", err);
		}
	};

	const shareToSocial = (platform) => {
		const shareText = encodeURIComponent(whatsappTitle);
		const encodedUrl = encodeURIComponent(url);

		const shareUrls = {
			twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${shareText}`,
			facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
			linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
			whatsapp: `https://wa.me/?text=${shareText}%20${encodedUrl}`,
		};

		window.open(shareUrls[platform], "_blank", "width=600,height=400");
	};

	const handleConnectionToggle = (connectionId) => {
		setSelectedConnections(prev => 
			prev.includes(connectionId) 
				? prev.filter(id => id !== connectionId)
				: [...prev, connectionId]
		);
	};

	const handleSelectAllConnections = () => {
		if (selectedConnections.length === connections.length) {
			setSelectedConnections([]);
		} else {
			setSelectedConnections(connections.map(conn => conn._id));
		}
	};

	const sendMessageToConnection = async (connectionId) => {
		try {
			// Find or create chat
			let chatResponse = await findChat(connectionId, loggedInUser._id);
			let chatId;

			if (chatResponse.data.length === 0) {
				// Create new chat
				const createChatResponse = await createChat(connectionId, loggedInUser._id);
				chatId = createChatResponse.data._id;
			} else {
				chatId = chatResponse.data._id;
			}

			// Send message
			const messageData = {
				id: Date.now().toString(),
				chatId: chatId,
				senderId: loggedInUser._id,
				text: `${messageText}\n\n${url}`,
			};

			await addMessage(messageData);
		} catch (error) {
			console.error("Error sending message to connection:", error);
			throw error;
		}
	};

	const handleSendToConnections = async () => {
		if (selectedConnections.length === 0) {
			toast.error("Please select at least one connection");
			return;
		}

		setSendingMessages(true);
		const successCount = { success: 0, failed: 0 };

		try {
			await Promise.allSettled(
				selectedConnections.map(async (connectionId) => {
					try {
						await sendMessageToConnection(connectionId);
						successCount.success++;
					} catch (error) {
						successCount.failed++;
					}
				})
			);

			// Show result
			if (successCount.success > 0) {
				toast.success(`Successfully sent to ${successCount.success} connection(s)${successCount.failed > 0 ? `, failed to send to ${successCount.failed} connection(s)` : ''}`);
				setShowConnectionsModal(false);
				setSelectedConnections([]);
				setIsOpen(false);
			} else {
				toast.error("Failed to send messages to any connections");
			}
		} catch (error) {
			console.error("Error sending messages:", error);
			toast.error("An error occurred while sending messages");
		} finally {
			setSendingMessages(false);
		}
	};

	return (
		<div
			className={`share-popup ${theme === "dark" ? "dark-theme" : ""} ${
				isInvestor ? "investor-theme" : ""
			}`}
		>
			{isOpen && (
				<div className="popup-overlay" onClick={() => setIsOpen(false)}>
					<div className="popup-content" onClick={(e) => e.stopPropagation()}>
						<div className="popup-header">
							<h2>Share on Socials</h2>
							<button className="close-button" onClick={() => setIsOpen(false)}>
								×
							</button>
						</div>

						<div className="social-icons">
							<button
								className="social-button twitter"
								onClick={() => shareToSocial("twitter")}
							>
								<FaTwitter />
							</button>
							<button
								className="social-button facebook"
								onClick={() => shareToSocial("facebook")}
							>
								<FaFacebook />
							</button>
							<button
								className="social-button linkedin"
								onClick={() => shareToSocial("linkedin")}
							>
								<FaLinkedin />
							</button>
							<button
								className="social-button whatsapp"
								onClick={() => shareToSocial("whatsapp")}
								aria-label="Share on WhatsApp"
							>
								<FaWhatsapp />
							</button>
							<button
								className="social-button connections"
								onClick={() => setShowConnectionsModal(true)}
								aria-label="Send to Connections"
							>
								<FaComments />
							</button>
						</div>

						<div className="whatsapp-title">
							<label htmlFor="whatsapp-title">WhatsApp Message:</label>
							<input
								id="whatsapp-title"
								type="text"
								value={whatsappTitle}
								onChange={(e) => setWhatsappTitle(e.target.value)}
								placeholder="Enter Title for WhatsApp"
								className="whatsapp-title-input"
							/>
						</div>

						<div className="copy-link">
							<button className="copy-button" onClick={handleCopyLink}>
								<span className="url-text">{url}</span>
								<FiCopy />
							</button>
						</div>

						{showCopyAlert && (
							<div className="copy-alert">Link copied to clipboard!</div>
						)}
					</div>
				</div>
			)}

			{/* Connections Modal */}
			{showConnectionsModal && (
				<div className="connections-modal-overlay" onClick={() => setShowConnectionsModal(false)}>
					<div className="connections-modal-content" onClick={(e) => e.stopPropagation()}>
						<div className="connections-modal-header">
							<h3>Send to Connections</h3>
							<button className="close-button" onClick={() => setShowConnectionsModal(false)}>
								×
							</button>
						</div>

						<div className="message-input-section">
							<label htmlFor="message-text">Message:</label>
							<textarea
								id="message-text"
								value={messageText}
								onChange={(e) => setMessageText(e.target.value)}
								placeholder="Enter your message..."
								className="message-textarea"
								rows={3}
							/>
						</div>

						<div className="connections-section">
							<div className="connections-header">
								<h4>Select Connections ({selectedConnections.length}/{connections.length})</h4>
								<button 
									className="select-all-button"
									onClick={handleSelectAllConnections}
								>
									{selectedConnections.length === connections.length ? 'Deselect All' : 'Select All'}
								</button>
							</div>

							<div className="connections-list">
								{connections.length > 0 ? (
									connections.map((connection) => (
										<div key={connection._id} className="connection-item">
											<label className="connection-checkbox">
												<input
													type="checkbox"
													checked={selectedConnections.includes(connection._id)}
													onChange={() => handleConnectionToggle(connection._id)}
												/>
												<div className="connection-info">
													<img
														src={connection.profilePicture || defaultAvatar}
														alt={`${connection.firstName} ${connection.lastName}`}
														className="connection-avatar"
													/>
													<div className="connection-details">
														<span className="connection-name">
															{connection.firstName} {connection.lastName}
														</span>
														<span className="connection-designation">
															{connection.designation}
														</span>
													</div>
												</div>
											</label>
										</div>
									))
								) : (
									<p className="no-connections">No connections found</p>
								)}
							</div>
						</div>

						<div className="connections-modal-actions">
							<button 
								className="cancel-button"
								onClick={() => setShowConnectionsModal(false)}
							>
								Cancel
							</button>
							<button 
								className="send-button"
								onClick={handleSendToConnections}
								disabled={sendingMessages || selectedConnections.length === 0}
							>
								{sendingMessages ? 'Sending...' : `Send to ${selectedConnections.length} connection(s)`}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default SharePopup;
